/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  Button,
  Alert,
  Skeleton,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoIcon from "@mui/icons-material/Info";
import AddCircleIcon from "@mui/icons-material/AddCircleOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useSelector, useDispatch } from "react-redux";
import { meHandler } from "src/store/reducers/meSlice";
// import VideoPreviewer from "src/components/shared/atoms/VideoPreviewer";
import DialogBoxLayout from "src/components/shared/atoms/dialog";
import UPDATE_DEFAULT_PAYMENT_METHOD_FOR_CUSTOMERS from "src/graphql/mutation/settings/paymentMethod/updatePaymentMethod";
import REMOVE_PAYMENT_METHOD_FOR_CUSTOMERS from "src/graphql/mutation/settings/paymentMethod/removePaymentMethod";
import GET_CURRENT_SUBSCRIPTION from "src/graphql/query/settings/getCurrentSubscriptions";
import CANCEL_SUBSCRIPTION from "src/graphql/mutation/settings/cancelSubscription";
import { useNavigate } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { enqueueSnackbar } from "notistack";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import expressConfig from "src/config/express.json";
import PaymentCard from "./paymentCard";
import BillingHistory from "./billingHistory";
import AddNewMethodModal from "./AddNewMethodModal";
import {
  ICurrentSubscriptionData,
  IInvoiceArrayObject,
  IInvoice,
  IPaymentMethod,
} from "./types";
import useStyle from "./Styles";

const env = process.env.NODE_ENV || "development";
const stripePublishableKey = expressConfig[env].STRIPE_PUB_KEY;
const stripePromise = loadStripe(stripePublishableKey);

const Billing = () => {
  const { classes } = useStyle();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { stripeId: customerId, subscriptionId } = useActiveUserDetail();

  const { me }: any = useSelector((state: any) => state.me);

  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState<
    (() => void) | null
  >(null);

  const [currentSubscriptionData, setCurrentSubscriptionData] =
    useState<ICurrentSubscriptionData>();

  const [openDialog, setOpenDialog] = useState(false);
  const [openCancelSubDialog, setOpenCancelSubDialog] = useState(false);
  const [cardBrand, setCardBrand] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [toBeDeletePaymentId, setToBeDeletePaymentId] = useState("");
  const [toBeDefaultPaymentId, setToBeDefaultPaymentId] = useState("");
  const [inVoiceDataArray, setInVoiceDataArray] = useState<
    IInvoiceArrayObject[]
  >([]);

  const [
    getCurrentSubscription,
    {
      loading: getCurrentSubscriptionLoading,
      error: getCurrentSubscriptionError,
      data: getCurrentSubscriptionResult,
    },
  ] = useLazyQuery(GET_CURRENT_SUBSCRIPTION, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (me && (me.subscriptionId || (customerId && me.plans === "enterprise")))
      getCurrentSubscription({
        variables: {
          subscriptionId: me.subscriptionId || "",
          stripeCustomerId: customerId,
        },
      });
  }, [me, customerId]);

  useEffect(() => {
    const subscriptions =
      getCurrentSubscriptionResult?.getCurrentSubscriptions?.result;

    if (subscriptions && subscriptions.invoices) {
      setCurrentSubscriptionData(subscriptions);

      const updated = subscriptions.invoices.map(
        (item: IInvoice, index: number) => ({
          ...item,
          id: index + 1,
          invoice: item.id,
        }),
      );

      setInVoiceDataArray(updated);
    } else {
      // optional: clear previous state if no data
      setCurrentSubscriptionData(undefined);
      setInVoiceDataArray([]);
    }
  }, [getCurrentSubscriptionResult]);

  useEffect(() => {
    if (getCurrentSubscriptionError) {
      enqueueSnackbar(getCurrentSubscriptionError.message, {
        variant: "error",
      });
    }
  }, [getCurrentSubscriptionError]);

  // update payment method work
  const [
    updateDefaultPaymentMethodForCustomers,
    {
      loading: updateDefaultPaymentMethodForCustomersLoading,
      error: updateDefaultPaymentMethodForCustomersError,
      data: updateDefaultPaymentMethodForCustomersResult,
    },
  ] = useMutation(UPDATE_DEFAULT_PAYMENT_METHOD_FOR_CUSTOMERS);

  useEffect(() => {
    if (
      updateDefaultPaymentMethodForCustomersResult &&
      updateDefaultPaymentMethodForCustomersResult.updateDefaultPaymentMethodForCustomer
    ) {
      if (
        updateDefaultPaymentMethodForCustomersResult.updateDefaultPaymentMethodForCustomer &&
        currentSubscriptionData
      ) {
        const updatingPaymentMethodArray =
          currentSubscriptionData.paymentMethods;
        const updatedPayment = updatingPaymentMethodArray.find(
          (pm) => pm.id === toBeDefaultPaymentId,
        );

        if (!updatedPayment) return; // If not found, return original array

        // Update the found object to isDefault: true
        updatedPayment.isDefault = true;

        // Update all other objects to isDefault: false
        const updatedList = updatingPaymentMethodArray.map((pm) =>
          pm.id === toBeDefaultPaymentId
            ? updatedPayment
            : { ...pm, isDefault: false },
        );

        // Move the newly set default object to the top
        const updatedPaymentMethodArray = [
          updatedPayment,
          ...updatedList.filter((pm) => pm.id !== toBeDefaultPaymentId),
        ];

        setCurrentSubscriptionData({
          ...currentSubscriptionData,
          paymentMethods: updatedPaymentMethodArray,
        });
      }
    }
  }, [updateDefaultPaymentMethodForCustomersResult]);

  useEffect(() => {
    if (updateDefaultPaymentMethodForCustomersError) {
      enqueueSnackbar(updateDefaultPaymentMethodForCustomersError.message, {
        variant: "error",
      });
    }
  }, [updateDefaultPaymentMethodForCustomersError]);

  // remove payment method work

  const [
    removePaymentMethodForCustomers,
    {
      loading: removePaymentMethodForCustomersLoading,
      error: removePaymentMethodForCustomersError,
      data: removePaymentMethodForCustomersResult,
    },
  ] = useMutation(REMOVE_PAYMENT_METHOD_FOR_CUSTOMERS);

  useEffect(() => {
    if (
      removePaymentMethodForCustomersResult &&
      removePaymentMethodForCustomersResult.removePaymentMethodForCustomer
    ) {
      if (
        removePaymentMethodForCustomersResult.removePaymentMethodForCustomer &&
        currentSubscriptionData
      ) {
        setOpenDialog(false);

        const paymentMethodsList = currentSubscriptionData?.paymentMethods;
        const updatedPaymentMethodsList = paymentMethodsList?.filter(
          (item) => item.id !== toBeDeletePaymentId,
        );

        setCurrentSubscriptionData({
          ...currentSubscriptionData,
          paymentMethods: updatedPaymentMethodsList,
        });

        enqueueSnackbar("Payment method has been deleted", {
          variant: "success",
        });
      }
    }
  }, [removePaymentMethodForCustomersResult]);

  useEffect(() => {
    if (removePaymentMethodForCustomersError) {
      enqueueSnackbar(removePaymentMethodForCustomersError.message, {
        variant: "error",
      });
    }
  }, [removePaymentMethodForCustomersError]);

  // adding payment method work

  const handleDelete = (paymentId: string, brand: string, expiry: string) => {
    setOpenDialog(true);
    setCardBrand(brand);
    setCardExpiry(expiry);
    setToBeDeletePaymentId(paymentId);
  };

  const handleDeletePayment = () => {
    removePaymentMethodForCustomers({
      variables: {
        paymentMethodId: toBeDeletePaymentId,
      },
    });
  };

  const handleSetDefault = (paymentMethodId: string) => {
    setToBeDefaultPaymentId(paymentMethodId);
    updateDefaultPaymentMethodForCustomers({
      variables: {
        customerId,
        paymentMethodId,
      },
    });
  };

  const handleAddPayment = () => {
    if (openPaymentMethodModal) {
      openPaymentMethodModal(); // call the stored function
    }
  };

  // update payment method work
  const [
    cancelSubscription,
    {
      loading: cancelSubscriptionLoading,
      error: cancelSubscriptionError,
      data: cancelSubscriptionResult,
    },
  ] = useMutation(CANCEL_SUBSCRIPTION);

  useEffect(() => {
    if (
      cancelSubscriptionResult &&
      cancelSubscriptionResult.cancelSubscription
    ) {
      if (cancelSubscriptionResult.cancelSubscription) {
        if (cancelSubscriptionResult.cancelSubscription.success) {
          const {
            stripe_subscription_status: cancelledStatus,
            plans: cancelledPlan,
          } = cancelSubscriptionResult.cancelSubscription.result;
          // updating ME data
          dispatch(
            meHandler({
              stripe_subscription_status: cancelledStatus,
              plans: cancelledPlan,
            }),
          );
          enqueueSnackbar("Subscription has been successfully deleted", {
            variant: "success",
          });
          setOpenCancelSubDialog(false);
          navigate("/settings/billing");
        }
      }
    }
  }, [cancelSubscriptionResult]);

  useEffect(() => {
    if (cancelSubscriptionError) {
      enqueueSnackbar(cancelSubscriptionError.message, {
        variant: "error",
      });
    }
  }, [cancelSubscriptionError]);

  const handleCancelSubscription = () => {
    cancelSubscription({
      variables: {
        subscriptionId,
      },
    });
  };

  const renderPlanHeader = (
    title: string,
    subtitle: string,
    inMiddle: boolean = false,
    showVideo: boolean = false,
  ) => (
    <Box
      className={classes.planHeadBox}
      sx={{
        marginTop: inMiddle ? "20px" : 0,
        zIndex: showVideo ? 1 : 0,
      }}
    >
      {showVideo ? (
        <Box className={classes.videoHeader}>
          <Typography className={classes.planHeadBoxHeading}>
            {title}
          </Typography>

          {/* <VideoPreviewer videoId="ELf2MlpUUNY" /> */}
        </Box>
      ) : (
        <Typography className={classes.planHeadBoxHeading}>{title}</Typography>
      )}

      <Typography className={classes.planHeadBoxSubHeading}>
        {subtitle}
      </Typography>
    </Box>
  );

  const freeTrialContainer = () => (
    <Box className={classes.freeTrialBox}>
      <Typography variant="h6" className={classes.freeTrialHeadText}>
        Free Trial
      </Typography>

      <Typography color="textSecondary" className={classes.freeTrialExpiryText}>
        Your Free trial expires on {me?.endDate.split("T")[0]}
        {/* {new Date(Number(me?.endDate)).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} */}
      </Typography>

      <Typography
        color="textSecondary"
        className={classes.freeTrialMessageText}
      >
        Enjoying your free trial? Get the most out of our services by upgrading
        to a premium plan today!
      </Typography>
      <Button
        onClick={() => navigate("/settings/upgrade-plan")}
        className={classes.freeTrialUpgradePlanBtn}
      >
        Upgrade Plan
      </Button>

      <Typography color="textSecondary" className={classes.freeTrialCountText}>
        {me?.currentCount} out of {me?.maxCount} users
      </Typography>

      <LinearProgress
        variant="determinate"
        // value={maxCount > 0 ? (me.currentCount / me.maxCount) * 100 : 0}
        value={
          me?.maxCount && me.maxCount > 0
            ? (me.currentCount / me.maxCount) * 100
            : 0
        }
        sx={{ my: 1, height: 6, borderRadius: 3 }}
      />
    </Box>
  );

  // free trial plan
  if (me.plans === "freetrial" && !me.subscriptionId)
    return (
      <Box>
        {renderPlanHeader(
          "Current Subscription",
          "Current subscription details for your selected plan and billing information.",
          false,
          true,
        )}
        {freeTrialContainer()}
      </Box>
    );

  // no active plan
  if (
    me &&
    me.plans !== "freetrial" &&
    !me.subscriptionId &&
    me.plans !== "enterprise"
  ) {
    return (
      <Box>
        {renderPlanHeader(
          "Current Subscription",
          "Current subscription details for your selected plan and billing information.",
          false,
          true,
        )}

        <Box className={classes.freeTrialBox}>
          <Typography className={classes.freeTrialHeadText}>
            No Active Plan
          </Typography>

          <Typography
            color="textSecondary"
            className={classes.freeTrialExpiryText}
          >
            You currently don’t have an active plan. To continue using services,
            please choose a plan that fits your requirements.
          </Typography>

          <Button
            onClick={() => navigate("/settings/upgrade-plan")}
            className={classes.freeTrialUpgradePlanBtn}
          >
            Choose a Plan
          </Button>
        </Box>
      </Box>
    );
  }

  // me && me.plans !== "freetrial" && me.subscriptionId
  // or
  // me && me.plans === "freetrial" && me.subscriptionId
  // there is freetrial and also subscriptionId
  return (
    <Box className={classes.subscriptionDetailsContainer}>
      <Elements stripe={stripePromise}>
        <AddNewMethodModal
          stripeCustomerId={customerId}
          setCurrentSubscriptionData={setCurrentSubscriptionData}
          setOpenPaymentMethodModal={setOpenPaymentMethodModal}
        />
      </Elements>

      {/* current-subscription-header */}
      {renderPlanHeader(
        me &&
          me?.new_plan_status === "past_due" &&
          me?.new_plan !== "null" &&
          me?.subscriptionId
          ? "Subscription Payment Pending"
          : "Current Subscription",
        me &&
          me?.new_plan_status === "past_due" &&
          me?.new_plan !== "null" &&
          me?.subscriptionId
          ? "Your payment is currently past due."
          : "Current subscription details for your selected plan and billing information.",
        false,
        true,
      )}

      {/* Pending Plan Change Banner */}
      {me &&
        me?.subscriptionId &&
        me?.new_plan_status === "pending" &&
        me?.new_plan && (
          <Box
            sx={{
              backgroundColor: "#FFF4E5",
              border: "1px solid #FFB020",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <InfoIcon
              sx={{ color: "#FFB020", fontSize: 24, marginTop: "2px" }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#663C00", marginBottom: "4px" }}
              >
                Plan Change Scheduled
              </Typography>
              <Typography variant="body2" sx={{ color: "#996500" }}>
                Your plan will change from{" "}
                <strong style={{ textTransform: "capitalize" }}>
                  {me.plans}
                </strong>{" "}
                to{" "}
                <strong style={{ textTransform: "capitalize" }}>
                  {me.new_plan}
                </strong>{" "}
                on{" "}
                <strong>
                  {me.plan_change_effective_date
                    ? new Date(
                        me.plan_change_effective_date,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "your next billing date"}
                </strong>
                . You will not be charged until then.
              </Typography>
            </Box>
          </Box>
        )}

      {/* Past Due Plan Change Banner */}
      {me &&
        me?.subscriptionId &&
        me?.new_plan_status === "past_due" &&
        me?.new_plan !== "null" && (
          <Box className={classes.paymentErrorBox}>
            <Alert severity="error" sx={{ mb: 2 }}>
              We attempted to charge your payment method for the{" "}
              <strong style={{ textTransform: "capitalize" }}>
                {me.new_plan}
              </strong>{" "}
              plan, but it was <strong>unsuccessful</strong>. Your account is
              now in a <strong>past due</strong> state.
            </Alert>

            <Typography className={classes.paymentErrorMessage}>
              To avoid service disruption, please update your payment details or
              retry the payment.
            </Typography>
          </Box>
        )}
      {/*  */}

      {getCurrentSubscriptionLoading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Skeleton animation="wave" variant="text" sx={{ width: "100%" }} />
        </Box>
      ) : !currentSubscriptionData ? (
        <Box className={classes.freeTrialBox}>
          <Typography className={classes.freeTrialHeadText}>
            {me?.plans === "enterprise"
              ? "Enterprise Onboarding"
              : "No Subscription"}
          </Typography>

          <Typography
            color="textSecondary"
            className={classes.freeTrialExpiryText}
          >
            {me?.plans === "enterprise"
              ? "We are finalizing your custom enterprise setup. Please add your preferred payment method below to complete the onboarding process."
              : "There is no subscription existing, or subscription has been corrupted."}
          </Typography>
        </Box>
      ) : me && me.plans === "freetrial" ? (
        freeTrialContainer()
      ) : (
        <>
          <Box className={classes.subscriptionDataBox}>
            <Box display="flex" alignItems="center" gap={7}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  className={classes.subscriptionDataPackageName}
                >
                  {currentSubscriptionData.packageName ||
                    "Enterprise Package (Pending Setup)"}
                </Typography>
                {currentSubscriptionData.interval && (
                  <Chip
                    label={currentSubscriptionData.interval}
                    color="success"
                    size="small"
                  />
                )}
              </Box>

              {currentSubscriptionData.price > 0 && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" fontWeight="bold">
                    ${currentSubscriptionData.price}
                  </Typography>
                  <Typography color="textSecondary" sx={{ color: "#999" }}>
                    per {currentSubscriptionData.interval}
                  </Typography>
                  <Typography color="textSecondary" sx={{ color: "#999" }}>
                    per agent
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              color="textSecondary"
              className={classes.subscriptionDataText}
            >
              {/* Your subscription renews on{" "}
              {currentSubscriptionData.renewalDate.split("T")[0]} */}
              {currentSubscriptionData.renewalDate ? (
                <>
                  Your subscription renews on{" "}
                  {currentSubscriptionData.renewalDate.split("T")[0]}
                </>
              ) : (
                "Your subscription does not have a renewal date"
              )}
            </Typography>

            <Typography
              color="textSecondary"
              className={classes.subscriptionDataCountText}
            >
              {currentSubscriptionData.currentUsersCount} out of{" "}
              {currentSubscriptionData.maxUsersCount} users
            </Typography>

            <LinearProgress
              variant="determinate"
              value={
                currentSubscriptionData.maxUsersCount > 0
                  ? (currentSubscriptionData.currentUsersCount /
                      currentSubscriptionData.maxUsersCount) *
                    100
                  : 0
              }
              sx={{ my: 1, height: 6, borderRadius: 3 }}
            />
          </Box>

          {/* deleted subscription message */}
          {me.plans &&
            me.endDate &&
            me.stripe_subscription_status === "canceled" && (
              <Box className={classes.basicPlanAccessNullBox}>
                <ErrorOutlineIcon
                  sx={{ color: "#DC2626", height: "25px", width: "25px" }}
                />
                <Typography
                  color="textSecondary"
                  className={classes.subscriptionDataCountText}
                >
                  You have{" "}
                  <span style={{ fontWeight: "bold", color: "#DC2626" }}>
                    deleted
                  </span>{" "}
                  the subscription . You will still have access to{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {me.currentPlanName}
                  </span>{" "}
                  features until{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {new Date(me.endDate).toLocaleDateString()}
                  </span>
                  . After that, your subscription will end. To continue using
                  the platform without interruption, please upgrade to a new
                  plan.
                </Typography>
                <Button
                  onClick={() => navigate("/settings/upgrade-plan")}
                  className={classes.freeTrialUpgradePlanBtn}
                >
                  Upgrade Plan
                </Button>
              </Box>
            )}

          {/* basic plan limited feature message */}
          {/* {me.plans === "basic" && (
            <Box className={classes.basicPlanAccessNullBox}>
              <ErrorOutlineIcon
                sx={{ color: "#DC2626", height: "25px", width: "25px" }}
              />
              <Typography
                color="textSecondary"
                className={classes.subscriptionDataCountText}
              >
                <span style={{ fontWeight: "bold" }}>Basic Plan</span> has
                limited access to certain features. To regain full
                functionality, please consider upgrading your plan.
              </Typography>
              <Button
                onClick={() => navigate("/settings/upgrade-plan")}
                className={classes.freeTrialUpgradePlanBtn}
              >
                Upgrade Plan
              </Button>
            </Box>
          )} */}
        </>
      )}

      {/* card details component */}
      {renderPlanHeader(
        "Payment method",
        "Update your billing details and address",
        true,
      )}
      {getCurrentSubscriptionLoading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Skeleton animation="wave" variant="text" sx={{ width: "100%" }} />
        </Box>
      ) : (
        <Box className={classes.cardDetailsBox}>
          {/* card details left */}
          <Box className={classes.cardDetailsInnerBox}>
            <Typography className={classes.planHeadBoxHeading}>
              Card details
            </Typography>
            <Typography className={classes.planHeadBoxSubHeading}>
              {(currentSubscriptionData?.paymentMethods?.length ?? 0) > 0
                ? "Select default payment method"
                : "No payment methods found"}
            </Typography>
          </Box>

          {/* card details right */}
          <Box className={classes.cardDetailsData}>
            {currentSubscriptionData?.paymentMethods?.map(
              (card: IPaymentMethod) => (
                <PaymentCard
                  key={card.id}
                  paymentId={card.id}
                  brand={card.brand}
                  cvc={card.last4}
                  expDate={card.expiry}
                  isDefault={card.isDefault}
                  isExpired={false}
                  onDelete={handleDelete}
                  onSetDefault={() => handleSetDefault(card.id)}
                  loading={updateDefaultPaymentMethodForCustomersLoading}
                  toBeDefaultPaymentId={toBeDefaultPaymentId}
                />
              ),
            )}

            {/* add new payment method */}
            <Button
              className={classes.addPaymentMethodBtn}
              sx={{
                alignSelf:
                  (currentSubscriptionData?.paymentMethods?.length ?? 0) > 0
                    ? "flex-start"
                    : "flex-end",
              }}
              id="user_dropdown"
              aria-controls="demo-customized-menu"
              aria-haspopup="true"
              aria-expanded
              disableElevation
              onClick={handleAddPayment}
              variant="contained"
              startIcon={
                <AddCircleIcon className={classes.addPaymentMethodIcon} />
              }
            >
              Add New Payment Method
            </Button>
          </Box>
        </Box>
      )}

      {/* billing-history component */}
      {renderPlanHeader(
        "Billing history",
        "Access all your previous invoices",
        true,
      )}
      {getCurrentSubscriptionLoading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Skeleton animation="wave" variant="text" sx={{ width: "100%" }} />
        </Box>
      ) : !currentSubscriptionData ? (
        <Box className={classes.freeTrialBox}>
          <Typography
            color="textSecondary"
            className={classes.freeTrialExpiryText}
          >
            With no subscription, there is no billing details existing
          </Typography>
        </Box>
      ) : (
        <BillingHistory inVoiceDataArray={inVoiceDataArray} />
      )}

      {currentSubscriptionData &&
        me &&
        me.subscriptionId &&
        me.plans !== "freetrial" &&
        !me.new_plan &&
        !me.new_plan_status &&
        me.stripe_subscription_status !== "canceled" && (
          <Button
            className={classes.deleteSubscriptionBtn}
            onClick={() => setOpenCancelSubDialog(true)}
          >
            Cancel Your Subscription
          </Button>
        )}
      {/* cancel subscription dialog box */}
      <DialogBoxLayout
        btnText="cancel subscription"
        open={openCancelSubDialog}
        modalClose={() => setOpenCancelSubDialog(false)}
        actionHandler={handleCancelSubscription}
        text="You want to cancel this subscription?"
        loader={cancelSubscriptionLoading}
        dialogContentBody={
          currentSubscriptionData === undefined ? (
            <CircularProgress
              color="primary"
              sx={{ marginTop: "5px" }}
              size={25}
            />
          ) : (
            <Box className={classes.deleteDialogBox}>
              <Box display="flex" alignItems="center" gap={7}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="h6"
                    className={classes.deleteDialogBoxText}
                  >
                    {currentSubscriptionData.packageName}
                  </Typography>
                  <Chip
                    label={currentSubscriptionData.interval}
                    color="success"
                    size="small"
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" fontWeight="bold">
                    ${currentSubscriptionData.price}
                  </Typography>
                  <Typography color="textSecondary" sx={{ color: "#999" }}>
                    per {currentSubscriptionData.interval}
                  </Typography>
                </Box>
              </Box>
              <Typography color="textSecondary" sx={{ color: "#999" }}>
                {/* Renewal date:{" "}
                {currentSubscriptionData.renewalDate.split("T")[0]} */}
                {currentSubscriptionData.renewalDate ? (
                  <>
                    Renewal date{" "}
                    {currentSubscriptionData.renewalDate.split("T")[0]}
                  </>
                ) : (
                  "Your subscription does not have a renewal date"
                )}
              </Typography>
            </Box>
          )
        }
      />

      {/* delete payment card details */}
      <DialogBoxLayout
        btnText="delete"
        icon={
          <DeleteForeverIcon color="success" className={classes.modalIcon} />
        }
        open={openDialog}
        modalClose={() => setOpenDialog(false)}
        actionHandler={handleDeletePayment}
        text="You want to delete this card?"
        loader={removePaymentMethodForCustomersLoading}
        dialogContentBody={
          <Box className={classes.deletePaymentCardBox}>
            <Box className={classes.deletePaymentCardInnerBox}>
              <CreditCardIcon sx={{ color: "gray" }} />
              <Typography
                variant="body2"
                className={classes.deletePaymentCardInnerBoxCreditText}
              >
                Credit card
              </Typography>
            </Box>
            <Typography
              variant="body2"
              className={classes.deletePaymentCardInnerBoxCreditText}
            >
              {cardBrand} ending in {cardExpiry}
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};

export default Billing;
